import type { Texture } from '@anov/3d-core'
import { Color, RepeatWrapping, ShaderChunk, TextureLoader, Vector4 } from '@anov/3d-core'
import { demo03, demo04, main } from './groundBase64'

const TEXTURE_REPEAT_COUNT = 5000
const MASK_TEXTURE_REPEAT = 10
const DEFAULT_GROUND_COLOR = '#096ddb'
const DEFAULT_MASK_COLOR = '#ffffff'

const textureLoader = new TextureLoader()

const groundBaseTexture = textureLoader.load(demo03)
const groundMaskTexture = textureLoader.load(demo04)
const groundPlaneTexture = textureLoader.load(main)

const configureTexture = (texture: Texture, repeatX: number, repeatY: number) => {
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
  texture.repeat.set(repeatX, repeatY)
}

configureTexture(groundBaseTexture, TEXTURE_REPEAT_COUNT, TEXTURE_REPEAT_COUNT)
configureTexture(groundMaskTexture, MASK_TEXTURE_REPEAT, MASK_TEXTURE_REPEAT)
configureTexture(groundPlaneTexture, TEXTURE_REPEAT_COUNT, TEXTURE_REPEAT_COUNT)

export const uniforms = {
  _Texture: { value: groundBaseTexture },
  _Texture_ST: { value: new Vector4() },
  _Color: {
    value: new Vector4(
      new Color(DEFAULT_GROUND_COLOR).r,
      new Color(DEFAULT_GROUND_COLOR).g,
      new Color(DEFAULT_GROUND_COLOR).b,
      0.5,
    ),
  },
  _ColorS: { value: 1 },
  _TextureMask: { value: groundMaskTexture },
  _TexturePlane: { value: groundPlaneTexture },
  _MaskUV: { value: 1 },
  _ColorMask: {
    value: new Vector4(
      new Color(DEFAULT_MASK_COLOR).r,
      new Color(DEFAULT_MASK_COLOR).g,
      new Color(DEFAULT_MASK_COLOR).b,
      1,
    ),
  },
  _ColorMaskS: { value: 1 },
  _Speed: { value: new Vector4(0.0, 0.2, 0, 0) },
  _OP: { value: 1.0 },
  time: { value: 1.0 },
}

// Vertex shader
const vertexShader = /* glsl */`
    ${ShaderChunk.logdepthbuf_pars_vertex}
    
    bool isPerspectiveMatrix(mat4) {
        return true;
    }
    
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vPosition = worldPosition.xyz;
        ${ShaderChunk.logdepthbuf_vertex}
    }
`

// Fragment shader
const fragmentShader = /* glsl */`
    ${ShaderChunk.logdepthbuf_pars_fragment}  

    uniform sampler2D _Texture;
    uniform sampler2D _TexturePlane;
    uniform vec4 _Color;
    uniform float _ColorS;
    uniform sampler2D _TextureMask;
    uniform float _MaskUV;
    uniform vec4 _ColorMask;
    uniform float _ColorMaskS;
    uniform float time;
    uniform float progress;
    uniform float objSize;
    uniform bool isFadeIn;
    uniform vec4 center;
    uniform bool isApi;
    
    varying vec2 vUv;
    varying vec3 vPosition;

    void main() {
        vec4 highlightColor = vec4(0.0, 0.6, 1.0, 1.0);
        vec3 baseColor = vec3(0.2);
        vec4 outputColor = vec4(baseColor, 1.0);
        
        // todo 后期需要根据大小自动调整
        // 
        vec4 baseTexture = texture2D(_Texture,  vUv * 25.0);
        vec4 planeTexture = texture2D(_TexturePlane, vUv * 15.0);
        
        vec2 maskScale = vec2(_MaskUV) * 20.0;
        vec2 maskUV = vUv * maskScale;
        vec2 animatedMaskUV = vec2(time, time) + maskUV;
        
        outputColor.rgb = (
            (planeTexture * 0.09) +
            (_Color * baseTexture.r * _ColorS) +
            (highlightColor * (baseTexture.r * texture2D(_TextureMask, animatedMaskUV).r) * _ColorMaskS * 10.0)
        ).rgb;

        if (isApi) {
            outputColor.rgb = mix(vec3(0.0), outputColor.rgb, 0.55);
        }

        outputColor.a = 1.0;


        vec3 myWorldPosition = vPosition / objSize;
        if (isFadeIn) {
            float circle1 = step(distance(center.xyz / objSize, myWorldPosition), progress - 0.002);
            float circle2 = step(distance(center.xyz / objSize, myWorldPosition), progress);
            outputColor.rgb += vec3(circle2 - circle1) * 3.;
            outputColor.a = outputColor.a * circle2;
        } else {
            float circle = step(distance(center.xyz / objSize, myWorldPosition), progress);
            outputColor.a *= step(circle, 0.);
        }
        
        gl_FragColor = outputColor;
        ${ShaderChunk.logdepthbuf_fragment}
    }
`

export const ground = { vertexShader, fragmentShader, uniforms }